"use client";

import { useState } from "react";
import { PRICING } from "@/lib/pricing-data";
import { trackEvent } from "@/lib/analytics";
import { capturePostHogEvent } from "@/lib/analytics-posthog";
import CheckoutSummary from "@/components/billing/checkout-summary";

const PRICE_MAP = {
  monthly: PRICING.monthly.stripe.priceId,
  annual: PRICING.annual.stripe.priceId,
  business: PRICING.business.stripe.priceId,
} as const;

interface PricingCtaProps {
  plan: "monthly" | "annual" | "business";
  highlight: boolean;
}

export default function PricingCta({ plan }: PricingCtaProps) {
  // Show a plan-aware confirmation summary before the Stripe redirect (Rec 2):
  // it reinforces value and shows the exact charge so the Stripe page is no
  // longer a surprise. The summary owns the checkout POST + its own in-flight
  // guard, so the CTA button only opens it.
  const [showSummary, setShowSummary] = useState(false);

  function handleClick() {
    const upgradeParams = {
      cta_location: "pricing-card",
      target_plan: plan === "business" ? "business" : "pro",
      billing_cycle: plan === "annual" ? "annual" : "monthly",
    };
    trackEvent("upgrade_clicked", upgradeParams);
    capturePostHogEvent("upgrade_clicked", upgradeParams);
    setShowSummary(true);
  }

  return (
    <div>
      <button
        onClick={handleClick}
        className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-lg bg-accent px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-accent-dark"
      >
        {plan === "business"
          ? `Start Business for £${PRICING.business.price}/month`
          : plan === "monthly"
            ? `Start Pro for £${PRICING.monthly.price}/month`
            : `Start Pro for £${PRICING.annual.price}/year`}
      </button>
      {/* Mount only while open so close→reopen resets the summary's loading /
          error / in-flight state (matches the UpgradeModal pattern). */}
      {showSummary && (
        <CheckoutSummary
          open
          priceId={PRICE_MAP[plan]}
          planLabel={plan}
          onClose={() => setShowSummary(false)}
        />
      )}
    </div>
  );
}

// Full-width repeat CTA used at the bottom of the value section (D-17).
export function RepeatProCta() {
  const [showSummary, setShowSummary] = useState(false);

  function handleClick() {
    const upgradeParams = {
      cta_location: "pricing-repeat-cta",
      target_plan: "pro",
      billing_cycle: "monthly",
    };
    trackEvent("upgrade_clicked", upgradeParams);
    capturePostHogEvent("upgrade_clicked", upgradeParams);
    setShowSummary(true);
  }

  return (
    <div className="mt-8">
      <button
        onClick={handleClick}
        className="flex min-h-[52px] w-full items-center justify-center gap-2 rounded-lg bg-accent px-6 py-4 text-base font-semibold text-white transition-colors hover:bg-accent-dark"
        data-testid="repeat-pro-cta"
      >
        Start Pro for {`£${PRICING.monthly.price}/month`}
      </button>
      {showSummary && (
        <CheckoutSummary
          open
          priceId={PRICING.monthly.stripe.priceId}
          planLabel="monthly"
          onClose={() => setShowSummary(false)}
        />
      )}
    </div>
  );
}
